import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import Head from "../../layout/head/Head";
import LogoDark2x from "../../images/logo-dark2x.png";
import authService from "../../api/auth/authService";
import MfaLoginStep from "./MfaLoginStep";
import {
  fetchCurrentUser,
  setAuthenticated,
} from "../../store/slices/misc/currentUser";
import { AppDispatch } from "../../store/store";
import styles from "./Login.module.css";

interface LoginCredentials {
  email: string;
  password: string;
}

const SLIDES = [
  {
    headline: "From 3D model to priced BOQ",
    subtext:
      "ProcureX BIM turns your IFC files into accurate bills of quantities — automatically. No double entry, no spreadsheet errors, no missed line items.",
  },
  {
    headline: "Coordinate before you build",
    subtext:
      "Catch clashes early, assign issues to the right discipline, and resolve them inside the model — not on site. Keep every team aligned through the project lifecycle.",
  },
  {
    headline: "Procurement that speaks your model",
    subtext:
      "Link every quantity to your rate library and approved suppliers. Generate tender-ready BOQs in minutes, audit-ready from day one.",
  },
];

const SLIDE_INTERVAL_MS = 6000;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState<boolean>(false);
  const [passState, setPassState] = useState<boolean>(false);
  const [errorVal, setError] = useState<string>("");
  // second factor pending: the ticket lives in state only (a refresh returns to the password step)
  const [mfa, setMfa] = useState<{ mfaToken: string; setupRequired: boolean; email: string } | null>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginCredentials>();

  // ---- Forgot password ----
  const [fpOpen, setFpOpen] = useState(false);
  const [fpEmail, setFpEmail] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpMsg, setFpMsg] = useState("");
  const [fpErr, setFpErr] = useState("");

  const openForgot = (e: React.MouseEvent) => {
    e.preventDefault();
    setFpEmail(watch("email") || "");
    setFpMsg(""); setFpErr("");
    setFpOpen(true);
  };

  const submitForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = fpEmail.trim();
    if (!/.+@.+\..+/.test(email)) { setFpErr("Enter a valid email address"); return; }
    setFpLoading(true); setFpErr(""); setFpMsg("");
    try {
      await authService.forgotPassword(email);
      setFpMsg(`A password-reset link has been sent to ${email}. Check your inbox.`);
    } catch (error: any) {
      const status = error?.response?.status;
      const apiMsg = String(error?.response?.data?.message || "").toLowerCase();
      if (status === 404 || apiMsg.includes("not found") || apiMsg.includes("not register") || apiMsg.includes("does not exist")) {
        setFpErr("No account is registered with this email address.");
      } else if (status && status >= 500) {
        setFpErr("Couldn't send the reset email right now. Please try again shortly.");
      } else {
        setFpErr(error?.response?.data?.message || "Couldn't send the reset email. Please try again.");
      }
    } finally {
      setFpLoading(false);
    }
  };

  // Auto-advance carousel; resets whenever the active slide changes
  // (so clicking an indicator pauses for the full interval before next advance).
  useEffect(() => {
    const id = setTimeout(() => {
      setActiveSlide((prev) => (prev + 1) % SLIDES.length);
    }, SLIDE_INTERVAL_MS);
    return () => clearTimeout(id);
  }, [activeSlide]);

  const onFormSubmit = async (formData: LoginCredentials) => {
    setLoading(true);
    setError("");

    try {
      const response = await authService.login({
        email: formData.email,
        password: formData.password,
        applicationType: "Buyer",
        isEmail: true,
      });

      if (response.status && response.mfaRequired && response.mfaToken) {
        setMfa({ mfaToken: response.mfaToken, setupRequired: !!response.setupRequired, email: formData.email });
      } else if (response.status) {
        dispatch(setAuthenticated(true));
        await dispatch(fetchCurrentUser());
        navigate("/dashboard");
      } else {
        setError(response.message || "Login failed. Please try again.");
      }
    } catch (error: any) {
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Login failed. Please check your credentials.";
      // explicit, not a neutral "invalid credentials": the same wording as the forgot-password dialog
      setError(/user not found/i.test(String(msg)) ? "No account is registered with this email address." : msg);
    } finally {
      setLoading(false);
    }
  };

  const preventNav = (e: React.MouseEvent) => e.preventDefault();

  return (
    <>
      <Head title="Login" />
      <div className={styles.root}>
        {/* Brand panel */}
        <aside className={styles.brand} aria-hidden="true">
          <div className={styles.slideshowWrap}>
            <div className={styles.slideStage}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSlide}
                  className={styles.slide}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <h2 className={styles.slideHeadline}>
                    {SLIDES[activeSlide].headline}
                  </h2>
                  <p className={styles.slideSubtext}>
                    {SLIDES[activeSlide].subtext}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className={styles.indicators} role="tablist">
              {SLIDES.map((_, i) => (
                <motion.button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={i === activeSlide}
                  aria-label={`Slide ${i + 1} of ${SLIDES.length}`}
                  className={styles.indicator}
                  onClick={() => setActiveSlide(i)}
                  animate={{
                    width: i === activeSlide ? 32 : 6,
                    backgroundColor:
                      i === activeSlide
                        ? "rgba(255, 255, 255, 0.95)"
                        : "rgba(255, 255, 255, 0.28)",
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                />
              ))}
            </div>
          </div>

          <div className={styles.brandCopyright}>
            &copy; 2026 Procurex Technologies PVT LTD. All Rights Reserved.
          </div>
        </aside>

        {/* Form panel */}
        <main className={styles.form}>
          <div className={styles.formInner}>
            <div className={styles.formCard}>
              <img
                src={LogoDark2x}
                alt="ProcureX BIM"
                className={styles.formLogo}
              />
              <h1 className={styles.formTitle}>Sign in to ProcureX BIM</h1>
              {!mfa && (
                <p className={styles.formSubtitle}>
                  Welcome back. Please enter your credentials to continue.
                </p>
              )}

              {mfa && (
                <MfaLoginStep
                  mfaToken={mfa.mfaToken}
                  setupRequired={mfa.setupRequired}
                  email={mfa.email}
                  onBack={() => { setMfa(null); setError(""); }}
                  onSuccess={async () => {
                    dispatch(setAuthenticated(true));
                    await dispatch(fetchCurrentUser());
                    navigate("/dashboard");
                  }}
                />
              )}

              {!mfa && errorVal && (
                <div className={styles.formError} role="alert">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="12" y1="8" x2="12" y2="12" />
                    <line x1="12" y1="16" x2="12.01" y2="16" />
                  </svg>
                  <span>{errorVal}</span>
                </div>
              )}

              <form onSubmit={handleSubmit(onFormSubmit)} noValidate style={mfa ? { display: "none" } : undefined}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="email">
                    Email address
                  </label>
                  <div className={styles.inputWrap}>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      className={`${styles.input} ${
                        errors.email ? styles.inputError : ""
                      }`}
                      {...register("email", {
                        required: "This field is required",
                        pattern: { value: /^\S+@\S+\.\S+$/, message: "Enter a valid email address" },
                      })}
                    />
                  </div>
                  {errors.email && (
                    <span className={styles.errorText}>
                      {errors.email.message}
                    </span>
                  )}
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="password">
                    Password
                  </label>
                  <div className={styles.inputWrap}>
                    <input
                      id="password"
                      type={passState ? "text" : "password"}
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      className={`${styles.input} ${styles.inputWithIcon} ${
                        errors.password ? styles.inputError : ""
                      }`}
                      {...register("password", {
                        required: "This field is required",
                      })}
                    />
                    <button
                      type="button"
                      className={styles.passwordToggle}
                      onClick={() => setPassState(!passState)}
                      aria-label={
                        passState ? "Hide password" : "Show password"
                      }
                    >
                      {passState ? (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <span className={styles.errorText}>
                      {errors.password.message}
                    </span>
                  )}
                </div>

                <div className={styles.utilityRow}>
                  <label className={styles.checkboxLabel}>
                    <input type="checkbox" />
                    Remember me
                  </label>
                  <a href="#" onClick={openForgot} className={styles.linkPrimary}>
                    Forgot password?
                  </a>
                </div>

                <button
                  type="submit"
                  className={styles.submit}
                  disabled={loading}
                >
                  {loading ? (
                    <span
                      className={styles.spinner}
                      aria-label="Signing in"
                    />
                  ) : (
                    "Sign in"
                  )}
                </button>

                <div className={styles.helpRow}>
                  <a
                    href="#"
                    onClick={preventNav}
                    className={styles.helpLink}
                  >
                    Need help signing in? →
                  </a>
                </div>
              </form>
            </div>
          </div>

          <div className={styles.formFooter}>
            <a href="#" onClick={preventNav}>
              Terms &amp; Conditions
            </a>
            <span className={styles.separator}>·</span>
            <a href="#" onClick={preventNav}>
              Privacy Policy
            </a>
          </div>
        </main>
      </div>

      {/* Forgot-password modal */}
      {fpOpen && (
        <div
          onClick={() => setFpOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 3000, padding: 16 }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", maxWidth: 420, background: "#fff", borderRadius: 14, boxShadow: "0 20px 60px rgba(16,32,64,.28)", padding: "26px 26px 22px" }}
          >
            <div style={{ fontSize: 18, fontWeight: 700, color: "#1f2a37", marginBottom: 6 }}>Reset your password</div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.55, marginBottom: 18 }}>
              Enter your account email and we'll send you a link to set a new password.
            </div>

            {fpMsg ? (
              <div style={{ background: "#ecfdf5", border: "1px solid #a7f3d0", color: "#065f46", borderRadius: 9, padding: "12px 14px", fontSize: 13, lineHeight: 1.55 }}>
                {fpMsg}
              </div>
            ) : (
              <form onSubmit={submitForgot}>
                <label style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "#374151", marginBottom: 6 }}>Email address</label>
                <input
                  type="email"
                  autoFocus
                  value={fpEmail}
                  onChange={(e) => { setFpEmail(e.target.value); setFpErr(""); }}
                  placeholder="you@company.com"
                  style={{ width: "100%", boxSizing: "border-box", padding: "11px 13px", borderRadius: 9, border: `1px solid ${fpErr ? "#dc2626" : "#d1d5db"}`, fontSize: 14, outline: "none" }}
                />
                {fpErr && <div style={{ color: "#dc2626", fontSize: 12, marginTop: 6 }}>{fpErr}</div>}
                <button
                  type="submit"
                  disabled={fpLoading}
                  style={{ marginTop: 16, width: "100%", padding: "12px 16px", borderRadius: 9, border: "none", background: "#2b3fa0", color: "#fff", fontSize: 14, fontWeight: 600, cursor: fpLoading ? "default" : "pointer", opacity: fpLoading ? 0.75 : 1 }}
                >
                  {fpLoading ? "Sending…" : "Send reset link"}
                </button>
              </form>
            )}

            <button
              onClick={() => setFpOpen(false)}
              style={{ marginTop: 14, width: "100%", padding: "10px 16px", borderRadius: 9, border: "1px solid #e5e7eb", background: "#fff", color: "#374151", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}
            >
              {fpMsg ? "Done" : "Cancel"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Login;
